import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Table, Spinner, Alert, Dropdown, Badge, Form, Row, Col } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import './Admin.css';
import '../../components/product/Filtros.css';

function AdminPedidos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [updatingStatus, setUpdatingStatus] = useState(new Set());
  const [productosPrecios, setProductosPrecios] = useState({}); // Cache de precios desde Firebase
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroTelefono, setFiltroTelefono] = useState('');

  const estadosPermitidos = ['CREADO', 'COBRADO', 'ENTREGADO', 'CANCELADO'];

  // Obtener preciomenor desde Firebase si no está en el pedido
  const obtenerPrecioMenor = useCallback(async (productoId) => {
    if (productosPrecios[productoId]) {
      return productosPrecios[productoId];
    }

    try {
      const productoRef = doc(db, 'Productos', productoId);
      const productoSnap = await getDoc(productoRef);
      if (productoSnap.exists()) {
        const data = productoSnap.data();
        const preciomenor = Number(data.preciomenor) || 0;
        setProductosPrecios(prev => ({ ...prev, [productoId]: preciomenor }));
        return preciomenor;
      }
    } catch (e) {
      console.error(`[AdminPedidos] Error obteniendo preciomenor para ${productoId}:`, e);
    }
    return 0;
  }, [productosPrecios]);


  const toggleRow = (pedidoId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pedidoId)) {
        newSet.delete(pedidoId);
      } else {
        newSet.add(pedidoId);
      }
      return newSet;
    });
  };

  const cambiarEstado = async (pedidoId, nuevoEstado) => {
    if (!pedidoId || typeof pedidoId !== 'string') {
      console.error('[AdminPedidos] ID de pedido inválido:', pedidoId);
      alert('Error: ID de pedido inválido');
      return;
    }
    
    setUpdatingStatus(prev => new Set(prev).add(pedidoId));
    try {
      console.log('[AdminPedidos] Cambiando estado:', { pedidoId, nuevoEstado });
      const url = `/pedidos/${pedidoId}`;
      console.log('[AdminPedidos] URL:', url);
      await ApiService.put(url, { estado: nuevoEstado });
      
      // Actualizar el estado localmente
      setItems(prev => prev.map(item => 
        item._id === pedidoId ? { ...item, estado: nuevoEstado } : item
      ));
    } catch (e) {
      console.error('[AdminPedidos] Error al actualizar estado:', e);
      console.error('[AdminPedidos] Error status:', e?.status);
      console.error('[AdminPedidos] Error payload:', e?.payload);
      alert(e?.payload?.message || e?.message || 'Error al actualizar el estado');
    } finally {
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(pedidoId);
        return newSet;
      });
    }
  };

  const getEstadoBadgeVariant = (estado) => {
    switch (estado) {
      case 'CREADO': return 'warning';
      case 'COBRADO': return 'cobrado';
      case 'ENTREGADO': return 'success';
      case 'CANCELADO': return 'danger';
      default: return 'secondary';
    }
  };

  const getMetodoPagoLabel = (metodoPago) => {
    switch (metodoPago) {
      case 'efectivo': return 'Efectivo';
      case 'mercadopago': return 'Mercado Pago';
      default: return metodoPago || 'Mercado Pago';
    }
  };

  const calcularTipoCompra = (pedido) => {
    if (!pedido.productos || !Array.isArray(pedido.productos)) return 'minorista';
    
    const cantidadTotal = pedido.productos.reduce((acc, p) => acc + (Number(p.cantidad) || 0), 0);
    const subtotalMayorista = pedido.productos.reduce((acc, p) => {
      const precio = Number(p.precio) || 0;
      const cantidad = Number(p.cantidad) || 0;
      return acc + (precio * cantidad);
    }, 0);
    
    // Regla mayorista: 6 o más productos Y subtotal con precio mayorista >= 60000
    const esMayorista = subtotalMayorista >= 60000 && cantidadTotal >= 6;
    return esMayorista ? 'mayorista' : 'minorista';
  };

  // Función helper para obtener la fecha de un pedido
  const obtenerFechaPedido = (pedido) => {
    if (pedido.fechaCreacion) {
      return new Date(pedido.fechaCreacion).getTime();
    }
    if (pedido.createdAt) {
      if (typeof pedido.createdAt?.toDate === 'function') {
        return pedido.createdAt.toDate().getTime();
      }
      return new Date(pedido.createdAt).getTime();
    }
    if (pedido.fecha) {
      return new Date(pedido.fecha).getTime();
    }
    return 0; // Si no tiene fecha, ponerlo al final
  };

  useEffect(() => {
    const fetchFromApi = async () => {
      try {
        const data = await ApiService.get('/pedidos');
        const list = Array.isArray(data)
          ? data
          : (data?.pedidos || data?.data || data?.items || data?.results || data?.result?.items || data?.data?.items || []);
        console.log('[AdminPedidos] Pedidos API raw:', data);
        console.log('[AdminPedidos] Pedidos API len:', list?.length || 0);
        
        // Ordenar por fecha descendente (más recientes primero)
        const listOrdenado = (list || []).sort((a, b) => {
          const fechaA = obtenerFechaPedido(a);
          const fechaB = obtenerFechaPedido(b);
          return fechaB - fechaA; // Orden descendente
        });
        
        setItems(listOrdenado);
        if (!list || list.length === 0) setError('No hay pedidos para mostrar');
      } catch (e) {
        console.error('[AdminPedidos] Error API:', e);
        if (e?.status === 401 || e?.status === 403) {
          setError('No autorizado. Iniciá sesión para ver los pedidos.');
        } else {
        setError(e?.message || 'No se pudieron cargar los pedidos');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchFromApi();
  }, []);

  // Cargar preciomenor desde Firebase cuando se expande un pedido
  useEffect(() => {
    const cargarPreciosMenores = async () => {
      const pedidosExpandidos = Array.from(expandedRows);
      if (pedidosExpandidos.length === 0) return;

      const pedidosNecesarios = items.filter(p => expandedRows.has(p._id));
      
      for (const pedido of pedidosNecesarios) {
        if (!pedido.productos || !Array.isArray(pedido.productos)) continue;
        
        for (const producto of pedido.productos) {
          const productoId = producto.id;
          if (!productoId) continue;
          
          // Si no tiene preciomenor guardado, buscarlo desde Firebase
          if (producto.preciomenor == null || producto.preciomenor === undefined) {
            await obtenerPrecioMenor(productoId);
          }
        }
      }
    };

    cargarPreciosMenores();
  }, [expandedRows, items, obtenerPrecioMenor]);

  // Filtrar items según los filtros aplicados
  const itemsFiltrados = useMemo(() => {
    return items.filter(item => {
      // Filtro por estado
      if (filtroEstado && item.estado !== filtroEstado) {
        return false;
      }
      
      // Filtro por nombre
      if (filtroNombre) {
        const nombre = (item.nombreContacto || '').toLowerCase();
        if (!nombre.includes(filtroNombre.toLowerCase())) {
          return false;
        }
      }
      
      // Filtro por teléfono
      if (filtroTelefono) {
        const telefono = (item.telefonoContacto || '').toString();
        if (!telefono.includes(filtroTelefono)) {
          return false;
        }
      }
      
      return true;
    });
  }, [items, filtroEstado, filtroNombre, filtroTelefono]);

  return (
    <Container className="py-4">
      <h5 className="admin-section-title mb-4">Pedidos</h5>
      
      {/* Filtros */}
      <div className="filtros-container mb-4">
        <h5>Filtros</h5>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
              >
                <option value="">Todos los estados</option>
                {estadosPermitidos.map(estado => (
                  <option key={estado} value={estado}>{estado}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre..."
                value={filtroNombre}
                onChange={(e) => setFiltroNombre(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Teléfono</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar por teléfono..."
                value={filtroTelefono}
                onChange={(e) => setFiltroTelefono(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        {(filtroEstado || filtroNombre || filtroTelefono) && (
          <div className="mt-3">
            <small className="text-muted">
              Mostrando {itemsFiltrados.length} de {items.length} pedidos
            </small>
            <button
              className="btn btn-link btn-sm p-0 ms-2"
              onClick={() => {
                setFiltroEstado('');
                setFiltroNombre('');
                setFiltroTelefono('');
              }}
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {loading && <Spinner animation="border" size="sm" />}<br />
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {!loading && !error && items.length === 0 && (
        <Alert variant="info" className="mt-3">No hay pedidos para mostrar</Alert>
      )}
      {!loading && !error && items.length > 0 && itemsFiltrados.length === 0 && (
        <Alert variant="warning" className="mt-3">
          No se encontraron pedidos que coincidan con los filtros aplicados.
        </Alert>
      )}
      {!loading && !error && itemsFiltrados.length > 0 && (
        <div className="admin-pedidos-table" style={{ position: 'relative' }}>
          <Table hover responsive className="admin-table-modern">
          <thead>
            <tr>
              <th className="text-start" style={{ width: '32px' }}></th>
              <th className="text-start">#</th>
              <th className="text-start">Productos</th>
              <th className="text-start">Fecha</th>
              <th className="text-start">Total</th>
              <th className="text-start">Nombre</th>
              <th className="text-start">Teléfono</th>
              <th className="text-start">Pago</th>
              <th className="text-start">Estado</th>
            </tr>
          </thead>
          <tbody>
            {itemsFiltrados.map((o, idx) => {
              const pedidoId = o._id;
              if (!pedidoId) {
                console.warn('[AdminPedidos] Pedido sin _id:', o);
                return null;
              }
              const isExpanded = expandedRows.has(pedidoId);
              const isUpdating = updatingStatus.has(pedidoId);
              
              return (
                <React.Fragment key={pedidoId}>
                  <tr 
                    className={`admin-pedido-row ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleRow(pedidoId)}
                  >
                    <td className="text-start">
                      <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                    </td>
                    <td className="text-start text-muted">{itemsFiltrados.length - idx}</td>
                    <td className="text-start">
                      {Array.isArray(o.productos) && o.productos.length > 0
                        ? `${o.productos.length} producto${o.productos.length !== 1 ? 's' : ''}`
                        : '-'}
                    </td>
                    <td className="text-start text-muted">{
                      o.fechaCreacion
                        ? new Date(o.fechaCreacion).toLocaleString('es-AR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : (o.createdAt
                            ? (typeof o.createdAt?.toDate === 'function'
                                ? o.createdAt.toDate().toLocaleString('es-AR', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : new Date(o.createdAt).toLocaleString('es-AR', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }))
                            : o.fecha
                              ? new Date(o.fecha).toLocaleString('es-AR', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : '-')
                    }</td>
                    <td className="text-start fw-semibold">{
                      (() => {
                        const val = o.montoTotal != null ? o.montoTotal : (o.total != null ? o.total : o.monto);
                        return val != null ? `$${Number(val).toLocaleString()}` : '-';
                      })()
                    }</td>
                    <td className="text-start">{o.nombreContacto || '-'}</td>
                    <td className="text-start">{o.telefonoContacto || '-'}</td>
                    <td className="text-start">
                      <Badge bg={o.metodoPago === 'efectivo' ? 'info' : 'secondary'}>
                        {getMetodoPagoLabel(o.metodoPago)}
                      </Badge>
                    </td>
                    <td className="text-start" onClick={(e) => e.stopPropagation()}>
                      <Dropdown 
                        drop="down" 
                        align="end"
                        popperConfig={{
                          strategy: 'fixed',
                          modifiers: [
                            {
                              name: 'preventOverflow',
                              options: {
                                boundary: 'viewport',
                                padding: 8,
                              },
                            },
                            {
                              name: 'flip',
                              options: {
                                fallbackPlacements: ['top', 'bottom'],
                              },
                            },
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 4],
                              },
                            },
                          ],
                        }}
                      >
                        <Dropdown.Toggle 
                          variant="outline-secondary" 
                          size="sm"
                          disabled={isUpdating}
                          className="admin-status-toggle"
                          id={`dropdown-estado-${pedidoId}`}
                        >
                          {isUpdating ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <Badge bg={getEstadoBadgeVariant(o.estado)} className="admin-status-badge">
                              {o.estado || '-'}
                            </Badge>
                          )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="admin-status-menu">
                          {estadosPermitidos.map(estado => (
                            <Dropdown.Item
                              key={estado}
                              onClick={() => cambiarEstado(pedidoId, estado)}
                              active={o.estado === estado}
                              className={o.estado === estado ? 'active' : ''}
                            >
                              {estado}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
              </tr>
                  {isExpanded && Array.isArray(o.productos) && o.productos.length > 0 && (
                    <tr className="no-hover">
                      <td colSpan="9" className="admin-productos-expanded">
                        <div className="admin-productos-content">
                          <div className="admin-productos-list">
                            {(() => {
                              const tipoCompra = calcularTipoCompra(o);
                              // Usar el orden que ya viene del backend (agrupado por material, nombre desc)
                              const productosOrdenados = o.productos;
                              
                              // Usar contador de materiales del backend
                              // Si viene como Map de Mongoose, convertirlo a objeto
                              let contadorMateriales = {};
                              if (o.contadorMateriales) {
                                if (o.contadorMateriales instanceof Map) {
                                  // Convertir Map a objeto
                                  o.contadorMateriales.forEach((value, key) => {
                                    contadorMateriales[key] = value;
                                  });
                                } else if (typeof o.contadorMateriales === 'object') {
                                  // Ya es un objeto
                                  contadorMateriales = o.contadorMateriales;
                                }
                              }
                              
                              return (
                                <>
                                  {/* Contador por material */}
                                  {Object.keys(contadorMateriales).length > 0 && (
                                    <div className="admin-contador-materiales mb-3 p-3" style={{ 
                                      backgroundColor: '#f8f9fa', 
                                      borderRadius: '8px',
                                      border: '1px solid #dee2e6'
                                    }}>
                                      <h6 className="mb-2 fw-bold">Contador por Material:</h6>
                                      <div className="d-flex flex-wrap gap-2">
                                        {Object.entries(contadorMateriales)
                                          .sort((a, b) => b[1] - a[1]) // Ordenar por cantidad descendente
                                          .map(([material, cantidad]) => (
                                            <Badge key={material} bg="secondary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
                                              {material}: {cantidad}
                                            </Badge>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Lista de productos ordenados */}
                                  {productosOrdenados.map((producto, pIdx) => {
                                    // Determinar precio según tipo de compra
                                    let precioUnitario = 0;
                                    if (tipoCompra === 'mayorista') {
                                      precioUnitario = Number(producto.precio) || 0;
                                    } else {
                                      // Minorista: usar preciomenor
                                      // Primero intentar desde el pedido guardado
                                      if (producto.preciomenor != null && producto.preciomenor !== undefined) {
                                        precioUnitario = Number(producto.preciomenor) || 0;
                                      } else {
                                        // Si no está guardado, usar el cache de Firebase
                                        precioUnitario = productosPrecios[producto.id] || 0;
                                        // Si tampoco está en cache, usar precio como último recurso
                                        if (precioUnitario === 0 && producto.precio != null) {
                                          console.warn(`[AdminPedidos] Producto ${producto.nombre} sin preciomenor disponible, usando precio como fallback`);
                                          precioUnitario = Number(producto.precio) || 0;
                                        }
                                      }
                                    }
                                    const subtotalItem = precioUnitario * (Number(producto.cantidad) || 0);
                                    
                                    return (
                                      <div key={producto._id || producto.id || pIdx} className="admin-producto-item">
                                        <div className="admin-producto-nombre">{producto.nombre || '-'}</div>
                                        <div className="admin-producto-details">
                                          <span className="text-muted">Cantidad: {producto.cantidad || '-'}</span>
                                          <span className="text-muted">•</span>
                                          <span className="text-muted">Precio ({tipoCompra}): ${precioUnitario.toLocaleString()}</span>
                                          <span className="admin-producto-subtotal">
                                            Subtotal: ${subtotalItem.toLocaleString()}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </>
                              );
                            })()}
                            <div className="admin-resumen-pedido">
                              {(() => {
                                const tipoCompra = calcularTipoCompra(o);
                                const total = tipoCompra === 'mayorista'
                                  ? o.productos.reduce((acc, p) => acc + ((Number(p.precio) || 0) * (Number(p.cantidad) || 0)), 0)
                                  : o.productos.reduce((acc, p) => {
                                      let preciomenor = Number(p.preciomenor) || 0;
                                      if (preciomenor === 0 && productosPrecios[p.id]) {
                                        preciomenor = productosPrecios[p.id];
                                      }
                                      if (preciomenor === 0 && p.precio) {
                                        preciomenor = Number(p.precio) || 0;
                                      }
                                      return acc + (preciomenor * (Number(p.cantidad) || 0));
                                    }, 0);
                                
                                return (
                                  <div className="admin-resumen-line admin-resumen-total">
                                    <span className="fw-bold">Total:</span>
                                    <span className="fw-bold"> ${total.toLocaleString()}</span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
        </div>
      )}
    </Container>
  );
}

export default AdminPedidos;


